import React, { useContext, useEffect, useState } from 'react'; // Step 1: Import useState
import { Grid, Flexbox, Divider } from '@sede-enterprise/shell-ds-react-framework';
import 'leaflet/dist/leaflet.css';
import { ReconFilterSection } from './DataReconciliationFilterSection';
import { AppContext } from '../../../../Context/AppContext';
import NcfReportingHomeHeader from '../NcfReportingUtils/NcfReportingHomeHeader/NcfReportingHomeHeader';

import {
  reconReportTotalVolume,
  reconReportVolumeTableData,
  reconReportNcfCategoryMismatchTableData,
  reconReportLastRefreshedDate,
  reconFilterOptions,
} from '../../../../api/services';
import Loader from '../../../Loader/Loader';
import DataReconciliationSideCard from './ReconReportingCharts/DataReconciliationSideCard/DataReconciliationSideCard';

import DeltaTable from './ReconReportingCharts/DeltaTable/DeltaTable';
import { ReconDataSourceFilter } from './ReconReportingCharts/ReconDataSourceFilter/ReconDataSourceFilter';
import PptReportingBarChart from './ReconReportingCharts/ReconTotalValuesTable/ReconTotalValuesTable';
import { CPM, TANSO } from '../../../../api/constants';
import {
  IReconDataPayload,
  IReconReportItemTanso,
  IReconReportLastRefreshedDate,
  IReconReportLastRefreshedDateTanso,
} from '../../../../Types/types';
import { convertKeys, errorNotifyMessage, isApiResponseEmpty, replaceKeyTansoToCpm } from '../../../Utils/utils';
import PipelineRunMessageOverlay from '../PipelineRunMessage/PipelineRunMessageOverlay';
import { notify } from '../../../../../CarbonDashboard/components/Toast/notify';

export const DataReconciliationHome = () => {
  const { dispatch: appDataDispatch } = useContext(AppContext).appData;
  const { state: reconState, dispatch: reconDispatch } = useContext(AppContext).reconReports;
  const [isLoading, setIsLoading] = useState(true); // Step 3: Initialize isLoading state
  const [isDataReconTotalVolumeDataEmpty, setIsDataReconTotalVolumeDataEmpty] = useState(false);
  const [isDataReconVolumeTableDataEmpty, setIsDataReconVolumeTableDataEmpty] = useState(false);

  useEffect(() => {
    appDataDispatch({ type: 'setCurrentPage', payload: '' });
  }, [appDataDispatch]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // Step 4: Set isLoading to true at the start
      try {
        if (reconState.ReconfilteredValues.reportType === 'NCF_CATEGORY_MISMATCH') {
          const categoryMismatchResponse = await reconReportNcfCategoryMismatchTableData(
            reconState.ReconfilteredValues,
          );
          reconDispatch({
            type: 'setReconReportNcfCategoryMismatchTableData',
            payload: categoryMismatchResponse.data,
          });
        } else {
          const volumeTableDataResponse = await reconReportVolumeTableData(
            reconState.ReconfilteredValues,
          );
          const convertedData = volumeTableDataResponse.data.data.map(
            (item: IReconReportItemTanso) => convertKeys(item),
          );
          // check if response is empty only for DELTA_DETAILS report type as other report types are good to be empty
          // as they are related to mismatch. It is good to have empty response for mismatch reports
          if (reconState.ReconfilteredValues.reportType === "DELTA_DETAILS") {
            setIsDataReconVolumeTableDataEmpty(
            isApiResponseEmpty(volumeTableDataResponse.data.data))
          }else {
            setIsDataReconVolumeTableDataEmpty(false);
          }
          const convertedTotalData = replaceKeyTansoToCpm(volumeTableDataResponse.data.totals);
          const updatedResponse = {
            ...volumeTableDataResponse.data,
            data: convertedData,
            totals: convertedTotalData,
          };
          reconDispatch({
            type: 'setReconReportVolumeTableData',
            payload: updatedResponse,
          });
        }
      } catch (error) {
         notify('error', errorNotifyMessage(error));
      } finally {
        setIsLoading(false); // Step 4: Set isLoading to false after fetching data
      }
    };
    if (reconState.ReconfilteredValues.dataSource !== '') {
      fetchData();
    }
  }, [reconDispatch, reconState.ReconfilteredValues]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true); // Step 4: Set isLoading to true at the start
      try {
        const reconFilterOptionsResponse = await reconFilterOptions({
          dataSource: reconState.ReconfilteredValues.dataSource,
        });

        // Dispatch the setReconFilterOptions action
        reconDispatch({
          type: 'setReconFilterOptions',
          payload: reconFilterOptionsResponse.data,
        });
        const totalVolumeResponse = await reconReportTotalVolume({
          ...reconState.ReconfilteredValues,
          reportType: 'DELTA_DETAILS',
        });
        const lastRefreshedDateResponse = await reconReportLastRefreshedDate(
          reconState.ReconfilteredValues,
        );
        const convertedTotalVolumeRespData = replaceKeyTansoToCpm(totalVolumeResponse.data);
        reconDispatch({
          type: 'setReconReportTotalVolume',
          payload: convertedTotalVolumeRespData as IReconDataPayload,
        });
        setIsDataReconTotalVolumeDataEmpty(
          isApiResponseEmpty(totalVolumeResponse.data));
        
        const item = lastRefreshedDateResponse.data;
        const newObj: IReconReportLastRefreshedDate = {
          ETRM_DATE: '',
          CPM_DATE: '',
          LOG: {
            REGION: '',
            COMMODITY: '',
            ERROR_HEADING: '',
            ERROR_DESCRIPTION: '',
            RUN_DATE: '',
            RUN_STATUS: '',
          },
        };
        for (const key in item) {
          if (Object.hasOwn(item, key)) {
            const newKeyName = key.includes(TANSO) ? key.replace(TANSO, CPM) : key;
            newObj[newKeyName as keyof IReconReportLastRefreshedDate] =
              item[key as keyof IReconReportLastRefreshedDateTanso];
          }
        }
        reconDispatch({
          type: 'setReconReportLastRefreshedDate',
          payload: newObj as IReconDataPayload,
        });
      } catch (error) {
         notify('error', errorNotifyMessage(error));
      } finally {
        setIsLoading(false); // Step 4: Set isLoading to false after fetching data
      }
    };
    if (reconState.ReconfilteredValues.dataSource !== '') {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reconDispatch, reconState.ReconfilteredValues.dataSource]);

  // useEffect to compute isAnyDataReconApiResponseEmpty based on individual states
    useEffect(() => {
      const isAnyResponseEmpty = isDataReconTotalVolumeDataEmpty ?? isDataReconVolumeTableDataEmpty;
      reconDispatch({
        type: 'setIsAnyDataReconApiResponseEmpty',
        payload: isAnyResponseEmpty,
      });
    }, [reconDispatch, isDataReconTotalVolumeDataEmpty, isDataReconVolumeTableDataEmpty, isDataReconVolumeTableDataEmpty]);

  const breadcrumbItems = [
    { name: 'Ncf Reporting', path: '/viewEditReport' },
    { name: 'Data Reconciliation Report' },
  ];

  return (
    <div style={{ height: '100%', background: '#F5F5F5' }} data-testid='recon-reporting'>
      {isLoading && <Loader loader={true} message={'Loading...'} />}

      <Flexbox gap='24px 24px' style={{ marginLeft: '15px', justifyContent: 'start' }}>
        <ReconFilterSection />
        <Grid gap='10px'>
          <Grid.Cell>
            <Flexbox justifyContent='space-between'>
              <NcfReportingHomeHeader
                title='Data Reconciliation Report'
                description='Detailed Data Reconciliation Report'
                breadcrumbItems={breadcrumbItems}
              />
              <DataReconciliationSideCard />
            </Flexbox>
          </Grid.Cell>
          <Divider size='medium' direction='horizontal' />
          <Grid.Cell>
            <ReconDataSourceFilter />
          </Grid.Cell>
          <div style={{ position: 'relative', zIndex: 1 }}>
            <PipelineRunMessageOverlay show={reconState.isAnyDataReconApiResponseEmpty} />
            <div>
              <Grid.Cell>
                 <PptReportingBarChart setIsDataReconTotalVolumeDataEmpty={setIsDataReconTotalVolumeDataEmpty} />
              </Grid.Cell>
              <Grid.Cell>
                  <DeltaTable />
              </Grid.Cell>
            </div>
          </div>
          {/* <Grid.Cell>
            <Flexbox gap="10px">
              <PptInScopeVsOutScope tradeName="Natural Gas" />
              <PptInScopeVsOutScope tradeName="Power" />
            </Flexbox>
          </Grid.Cell>
          <Grid.Cell>
            <Flexbox gap="10px">
              <PptInScopeNcfCatgChart tradeName="Natural Gas" />
              <PptInScopeNcfCatgChart tradeName="Power" />
            </Flexbox>
          </Grid.Cell> */}
        </Grid>
      </Flexbox>
    </div>
  );
};